NEW_FILE_CODE
<!-- views/advertisers/all-campaigns.vue -->
<template>
  <div class="all-campaigns-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>全部广告系列管理</h2>
      <div class="header-actions">
        <el-button type="success" icon="el-icon-plus" @click="handleBatchCreate">
          批量创建广告系列
        </el-button>
        <el-button
            type="primary"
            icon="el-icon-refresh"
            :loading="loading"
            @click="fetchAllData"
        >
          刷新数据
        </el-button>
      </div>
    </div>

    <!-- 广告主筛选 -->
    <el-card shadow="never" class="filter-card">
      <el-form :model="filter" inline>
        <el-form-item label="广告主">
          <el-select
              v-model="filter.advertiser_id"
              placeholder="全部广告主"
              clearable
              style="width: 300px"
              @change="handleAdvertiserChange"
          >
            <el-option
                v-for="item in advertisers"
                :key="item.advertiser_id"
                :label="`${item.advertiser_name} (${item.advertiser_id})`"
                :value="item.advertiser_id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="系列名称">
          <el-input
              v-model="filter.campaign_name"
              placeholder="请输入系列名称"
              clearable
              style="width: 200px"
          />
        </el-form-item>

        <el-form-item label="广告目标">
          <el-select
              v-model="filter.objective_type"
              placeholder="全部"
              clearable
              style="width: 140px"
          >
            <el-option
                v-for="item in objectiveOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="状态">
          <el-select
              v-model="filter.operation_status"
              placeholder="全部"
              clearable
              style="width: 120px"
          >
            <el-option label="启用" value="ENABLE" />
            <el-option label="暂停" value="DISABLE" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 统计信息 -->
    <el-row :gutter="20" class="stats-row">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">广告主数量</div>
            <div class="stat-value">{{ stats.advertiserCount }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">广告系列总数</div>
            <div class="stat-value">{{ stats.totalCampaigns }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">总消耗</div>
            <div class="stat-value">¥{{ formatMoney(stats.totalSpend) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">总展示</div>
            <div class="stat-value">{{ formatNumber(stats.totalImpressions) }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-card shadow="never">
      <el-table
          :data="filteredCampaigns"
          v-loading="loading"
          stripe
          max-height="800"
          style="width: 100%"
          @sort-change="handleSortChange"
      >
        <el-table-column
            prop="advertiser_name"
            label="广告主"
            width="250"
            fixed="left"
        >
          <template slot-scope="{ row }">
            <div class="advertiser-name">{{ row.advertiser_name.replace('-SUNNY-SINO-TT', '') }}</div>
            <div class="advertiser-id">{{ row.advertiser_id  }}</div>
          </template>
        </el-table-column>

        <el-table-column
            prop="campaign_id"
            label="系列 ID"
            width="180"
            fixed="left"
        >
          <template slot-scope="{ row }">
            <el-tooltip :content="row.campaign_id" placement="top">
              <span class="id-text">{{ row.campaign_id | truncate(16) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column
            prop="campaign_name"
            label="系列名称"
            min-width="200"
            fixed="left"
        >
          <template slot-scope="{ row }">
            <span class="campaign-name">{{ row.campaign_name }}</span>
          </template>
        </el-table-column>

        <el-table-column
            prop="objective_type"
            label="广告目标"
            width="120"
        >
          <template slot-scope="{ row }">
            {{ getObjectiveLabel(row.objective_type) }}
          </template>
        </el-table-column>

        <el-table-column
            prop="budget"
            label="预算"
            width="150"
            sortable="custom"
        >
          <template slot-scope="{ row }">
            <div v-if="row.budget_mode === 'BUDGET_MODE_INFINITE'">
              无限预算
            </div>
            <div v-else>
              <div>¥{{ formatBudget(row.budget) }}</div>
              <div class="budget-mode">{{ getBudgetModeLabel(row.budget_mode) }}</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
            prop="spend"
            label="消耗"
            width="120"
            sortable="custom"
        >
          <template slot-scope="{ row }">
            <div class="metric-value">¥{{ formatMoney(row.spend || 0) }}</div>
          </template>
        </el-table-column>

        <el-table-column
            prop="impressions"
            label="展示"
            width="100"
            sortable="custom"
        >
          <template slot-scope="{ row }">
            <div class="metric-value">{{ formatNumber(row.impressions || 0) }}</div>
          </template>
        </el-table-column>

        <el-table-column
            prop="clicks"
            label="点击"
            width="100"
            sortable="custom"
        >
          <template slot-scope="{ row }">
            <div class="metric-value">{{ formatNumber(row.clicks || 0) }}</div>
          </template>
        </el-table-column>

        <el-table-column
            prop="ctr"
            label="CTR"
            width="100"
            sortable="custom"
        >
          <template slot-scope="{ row }">
            <div class="metric-value">{{ formatPercent(row.ctr || 0) }}</div>
          </template>
        </el-table-column>

        <el-table-column
            prop="operation_status"
            label="状态"
            width="100"
        >
          <template slot-scope="{ row }">
            <status-tag :status="row.operation_status" />
          </template>
        </el-table-column>

        <el-table-column
            prop="create_time"
            label="创建时间"
            width="160"
            sortable="custom"
        >
          <template slot-scope="{ row }">
            <div class="time-text">{{ formatTime(row.create_time) }}</div>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="180" >
          <template slot-scope="{ row }">
            <el-button
                type="text"
                size="small"
                @click="handleViewAdgroups(row)"
            >
              查看广告组
            </el-button>
            <el-button
                v-if="row.operation_status === 'ENABLE'"
                type="text"
                size="small"
                @click="handlePause(row)"
            >
              暂停
            </el-button>
            <el-button
                v-else-if="row.operation_status === 'DISABLE'"
                type="text"
                size="small"
                @click="handleResume(row)"
            >
              启用
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
            background
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[20, 50, 100, 200]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 批量创建弹窗 -->
    <el-dialog
        title="批量创建广告系列"
        :visible.sync="batchDialogVisible"
        width="900px"
        @close="handleBatchDialogClose"
        class="batch-create-dialog"
    >
      <el-alert
          title="批量创建说明"
          type="info"
          :closable="false"
          show-icon        style="margin-bottom: 20px"
      >
        <div>1. 每行创建一个广告系列，格式：系列名称 | 预算金额（元）</div>
        <div>2. 示例：test1|100 表示创建名为 test1，预算 100 元的广告系列</div>
        <div>3. 支持批量粘贴 Excel 数据</div>
      </el-alert>

      <el-form :model="batchFormData" label-width="120px">
        <el-form-item label="选择广告主">
          <el-select
              v-model="batchFormData.advertiser_id"
              placeholder="请选择广告主"            style="width: 100%"
          >
            <el-option
                v-for="item in advertisers"
                :key="item.advertiser_id"
                :label="`${item.advertiser_name} (${item.advertiser_id})`"
                :value="item.advertiser_id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="广告目标" required>
          <el-select
              v-model="batchFormData.objective_type"
              placeholder="请选择广告目标"            style="width: 100%"
          >
            <el-option
                v-for="item in objectiveOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="应用推广类型" v-if="batchFormData.objective_type === 'APP_PROMOTION'">
          <el-select
              v-model="batchFormData.app_promotion_type"
              placeholder="请选择应用推广类型"            style="width: 100%"
          >
            <el-option
                v-for="item in appPromotionOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="预算模式" required>
          <el-radio-group v-model="batchFormData.budget_mode">
            <el-radio label="BUDGET_MODE_DYNAMIC_DAILY_BUDGET">动态每日预算</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="批量数据" required>
          <el-input
              v-model="batchFormData.batch_data"
              type="textarea"
              :rows="15"
              placeholder="请输入批量数据，每行一个广告系列&#10;格式：系列名称 | 预算金额&#10;例如：&#10;test1|100&#10;test2|200&#10;test3|150"
          />
          <div class="form-tip">已解析 {{ parsedCount }} 个广告系列</div>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="batchSubmitting" @click="handleBatchSubmit">
          开始批量创建
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { campaignApi } from '@/api/campaign'
import { advertiserApi } from '@/api/advertiserApi'
import StatusTag from '@/components/StatusTag'
import {
  OBJECTIVE_TYPE,
  OBJECTIVE_LABEL,
  BUDGET_MODE_LABEL,
  METRICS
} from '@/utils/constants'

export default {
  name: 'AllCampaigns',
  components: {
    StatusTag
  },
  data() {
    return {
      advertisers: [],
      campaigns: [],
      filteredCampaigns: [],
      loading: false,
      pagination: {
        page: 1,
        pageSize: 50,
        total: 0
      },
      sortField: '',
      sortOrder: '',
      filter: {
        advertiser_id: '',
        campaign_name: '',
        objective_type: '',
        operation_status: ''
      },
      objectiveOptions: [
        { value: OBJECTIVE_TYPE.APP_PROMOTION, label: '应用推广' },
        { value: OBJECTIVE_TYPE.WEB_CONVERSIONS, label: '网站转化' },
        { value: OBJECTIVE_TYPE.LEAD_GENERATION, label: '销售线索' }
      ],
      // ========== 新增：应用推广选项 ==========
      appPromotionOptions: [
        { value: 'APP_INSTALL', label: '应用安装' },
        { value: 'APP_RETARGETING', label: '应用重定向' },
        { value: 'MINIS', label: 'TikTok 小程序' }
      ],
      // 批量创建相关
      batchDialogVisible: false,
      batchSubmitting: false,
      batchFormData: {
        advertiser_id: '',
        objective_type: 'APP_PROMOTION',
        app_promotion_type: '',
        budget_mode: 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET',
        batch_data: ''
      },
      stats: {
        advertiserCount: 0,
        totalCampaigns: 0,
        totalSpend: 0,
        totalImpressions: 0
      }
    }
  },
  computed: {
    parsedCount() {
      if (!this.batchFormData.batch_data) return 0
      const lines = this.batchFormData.batch_data.trim().split('\n')
      return lines.filter(line => line.trim()).length
    }
  },
  mounted() {
    this.fetchAllData()
  },
  methods: {
    // 批量创建
    handleBatchCreate() {
      this.batchFormData = {
        advertiser_id: this.advertisers.length > 0 ? this.advertisers[0].advertiser_id : '',
        objective_type: 'APP_PROMOTION',
        app_promotion_type: '',
        budget_mode: 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET',
        batch_data: ''
      }
      this.batchDialogVisible = true
    },

    handleBatchDialogClose() {
      this.batchFormData = {
        advertiser_id: '',
        objective_type: 'APP_PROMOTION',
        app_promotion_type: '',
        budget_mode: 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET',
        batch_data: ''
      }
    },

    async handleBatchSubmit() {
      try {
        // 验证必填项
        if (!this.batchFormData.advertiser_id) {
          this.$message.error('请选择广告主')
          return
        }
        if (!this.batchFormData.objective_type) {
          this.$message.error('请选择广告目标')
          return
        }
        if (!this.batchFormData.batch_data.trim()) {
          this.$message.error('请输入批量数据')
          return
        }

        // 解析批量数据
        const lines = this.batchFormData.batch_data.trim().split('\n')
        const campaignList = []

        for (const line of lines) {
          const trimmedLine = line.trim()
          if (!trimmedLine) continue

          const parts = trimmedLine.split('|')
          if (parts.length < 2) {
            this.$message.warning(`格式错误：${trimmedLine}，请使用 名称 | 预算 格式`)
            continue
          }

          const campaignName = parts[0].trim()
          const budget = parseFloat(parts[1].trim())

          if (!campaignName) {
            this.$message.warning(`系列名称不能为空：${trimmedLine}`)
            continue
          }

          if (isNaN(budget) || budget < 10) {
            this.$message.warning(`预算金额无效：${trimmedLine}，至少 10 元`)
            continue
          }

          campaignList.push({
            campaign_name: campaignName,
            budget: budget * 100 // 转换为分
          })
        }

        if (campaignList.length === 0) {
          this.$message.error('没有有效的广告系列数据')
          return
        }

        // 确认创建
        await this.$confirm(`将创建 ${campaignList.length} 个广告系列，是否继续？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        this.batchSubmitting = true
        let successCount = 0
        let failCount = 0

        // 并发创建（限制并发数为 5）
        const batchSize = 5
        for (let i = 0; i < campaignList.length; i += batchSize) {
          const batch = campaignList.slice(i, i + batchSize)
          const promises = batch.map(async (campaign) => {
            try {
              const requestData = {
                advertiser_id: this.batchFormData.advertiser_id,
                campaign_name: campaign.campaign_name,
                objective_type: this.batchFormData.objective_type,
                campaign_type: 'REGULAR_CAMPAIGN',
                budget_mode: this.batchFormData.budget_mode,
                budget: campaign.budget,
                request_id: Date.now().toString() + Math.random().toString().slice(-6)
              }

              // 如果是 APP_PROMOTION 类型，添加 app_promotion_type
              if (this.batchFormData.objective_type === 'APP_PROMOTION' && this.batchFormData.app_promotion_type) {
                requestData.app_promotion_type = this.batchFormData.app_promotion_type
              }

              const res = await campaignApi.create(requestData)
              if (res.code === 0) {
                successCount++
              } else {
                failCount++
                console.error(`创建失败 ${campaign.campaign_name}:`, res.message)
              }
            } catch (error) {
              failCount++
              console.error(`创建异常 ${campaign.campaign_name}:`, error)
            }
          })

          await Promise.all(promises)
        }

        this.$message.success(`批量创建完成，成功 ${successCount} 个，失败 ${failCount} 个`)
        this.batchDialogVisible = false

        // 刷新列表
        if (successCount > 0) {
          await this.fetchAllData()
        }
      } catch (err) {
        if (err !== 'cancel') {
          this.$message.error('批量创建失败：' + err.message)
        }
      } finally {
        this.batchSubmitting = false
      }
    },

    // 获取所有广告主及其广告系列
    async fetchAllData() {
      this.loading = true
      try {
        // 1. 获取广告主列表
        const advertiserRes = await advertiserApi.getList({ appid: "fff",secret: "ttt" })
        if (advertiserRes.code === 0) {
          this.advertisers = advertiserRes.data.list || []
          this.stats.advertiserCount = this.advertisers.length

          // 2. 遍历所有广告主获取广告系列
          const allCampaigns = []
          const promises = this.advertisers.map(async (advertiser) => {
            try {
              const campaignRes = await campaignApi.getList({
                advertiserId: advertiser.advertiser_id,
                page: 1,
                pageSize: 100,
                // fields: ['campaign_id', 'campaign_name', 'objective_type', 'budget', 'budget_mode', 'operation_status', 'create_time', 'spend', 'impressions', 'clicks', 'ctr']
              })

              if (campaignRes.code === 0) {
                const campaignList = campaignRes.data.list || []
                // 为每个广告系列添加广告主信息
                campaignList.forEach(campaign => {
                  allCampaigns.push({
                    ...campaign,
                    advertiser_id: advertiser.advertiser_id,
                    advertiser_name: advertiser.advertiser_name
                  })
                })
              }
            } catch (error) {
              console.error(`获取广告主 ${advertiser.advertiser_id} 的广告系列失败:`, error)
            }
          })

          // 等待所有请求完成
          await Promise.all(promises)

          this.campaigns = allCampaigns
          this.pagination.total = allCampaigns.length
          this.stats.totalCampaigns = allCampaigns.length

          // 计算统计数据
          this.calculateStats(allCampaigns)

          // 应用筛选
          this.applyFilters()
        }
      } catch (error) {
        console.error('获取数据失败', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },

    // 计算统计数据
    calculateStats(campaigns) {
      this.stats.totalSpend = campaigns.reduce((sum, c) => sum + (c.spend || 0), 0)
      this.stats.totalImpressions = campaigns.reduce((sum, c) => sum + (c.impressions || 0), 0)
    },

    // 应用筛选条件
    applyFilters() {
      let filtered = [...this.campaigns]

      // 广告主筛选
      if (this.filter.advertiser_id) {
        filtered = filtered.filter(c => c.advertiser_id === this.filter.advertiser_id)
      }

      // 系列名称筛选
      if (this.filter.campaign_name) {
        filtered = filtered.filter(c =>
            c.campaign_name.toLowerCase().includes(this.filter.campaign_name.toLowerCase())
        )
      }

      // 广告目标筛选
      if (this.filter.objective_type) {
        filtered = filtered.filter(c => c.objective_type === this.filter.objective_type)
      }

      // 状态筛选
      if (this.filter.operation_status) {
        filtered = filtered.filter(c => c.operation_status === this.filter.operation_status)
      }

      // 排序
      if (this.sortField) {
        filtered.sort((a, b) => {
          const aVal = a[this.sortField] || 0
          const bVal = b[this.sortField] || 0
          return this.sortOrder === 'ascending' ? aVal - bVal : bVal - aVal
        })
      }

      this.filteredCampaigns = filtered
      this.pagination.total = filtered.length
    },

    handleAdvertiserChange() {
      this.handleSearch()
    },

    handleSearch() {
      this.pagination.page = 1
      this.applyFilters()
    },

    handleReset() {
      this.filter = {
        advertiser_id: '',
        campaign_name: '',
        objective_type: '',
        operation_status: ''
      }
      this.pagination.page = 1
      this.applyFilters()
    },

    handlePageChange(page) {
      this.pagination.page = page
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.applyFilters()
    },

    handleSortChange({ prop, order }) {
      if (order === 'ascending') {
        this.sortField = prop
        this.sortOrder = 'ASC'
      } else if (order === 'descending') {
        this.sortField = prop
        this.sortOrder = 'DESC'
      } else {
        this.sortField = ''
        this.sortOrder = ''
      }
      this.applyFilters()
    },

    handlePause(row) {
      this.$confirm('确定要暂停该广告系列吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await campaignApi.updateStatus({
            advertiser_id: row.advertiser_id,
            campaign_ids: [row.campaign_id],
            operation_status: 'DISABLE'
          })
          if (res.code === 0) {
            this.$message.success('已暂停')
            await this.fetchAllData()
          }
        } catch (error) {
          this.$message.error('操作失败')
        }
      }).catch(() => {})
    },

    handleResume(row) {
      this.$confirm('确定要启用该广告系列吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          const res = await campaignApi.updateStatus({
            advertiser_id: row.advertiser_id,
            campaign_ids: [row.campaign_id],
            operation_status: 'ENABLE'
          })
          if (res.code === 0) {
            this.$message.success('已启用')
            await this.fetchAllData()
          }
        } catch (error) {
          this.$message.error('操作失败')
        }
      }).catch(() => {})
    },

    handleViewAdgroups(row) {
      this.$router.push({
        path: '/adgroupsIndex',
        query: { campaignId: row.campaign_id, campaignName: row.campaign_name }
      })
    },

    formatBudget(budget) {
      if (budget === undefined || budget === null) return '-'
      return (budget).toFixed(2)
    },

    formatMoney(money) {
      if (money === undefined || money === null) return '0.00'
      return (money / 100).toFixed(2)
    },

    formatNumber(num) {
      if (num === undefined || num === null) return '0'
      if (num >= 100000000) {
        return `${(num / 100000000).toFixed(1)}亿`
      }
      if (num >= 10000) {
        return `${(num / 10000).toFixed(1)}万`
      }
      return num.toLocaleString()
    },

    formatPercent(val) {
      return `${(val * 100).toFixed(2)}%`
    },

    formatTime(timeStr) {
      if (!timeStr) return '-'
      return timeStr.replace(' ', '\n')
    },

    getObjectiveLabel(type) {
      return OBJECTIVE_LABEL[type] || type || '-'
    },

    getBudgetModeLabel(mode) {
      return BUDGET_MODE_LABEL[mode] || mode || '-'
    }
  },
  filters: {
    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? str.substring(0, len) + '...' : str
    }
  }
}
</script>

<style scoped>
.all-campaigns-page {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #1f2f3d;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.filter-card {
  margin-bottom: 16px;
}

.stats-row {
  margin-bottom: 16px;
}

.stat-card {
  text-align: center;
}

.stat-item {
  padding: 10px;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409eff;
}

.advertiser-name {
  font-weight: 400;
  color: #303133;
  font-size: 12px;
}

.advertiser-id {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}

.campaign-name {
  font-weight: 500;
  color: #303133;
}

.id-text {
  font-size: 12px;
  color: #909399;
}

.budget-mode {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.time-text {
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-line;
}

.metric-value {
  font-size: 13px;
  color: #303133;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

.batch-create-dialog .el-alert {
  padding: 10px 15px;
}

.batch-create-dialog .el-alert__content {
  font-size: 13px;
  line-height: 1.6;
}
</style>
